import { z } from 'zod';

import { requestJson } from './requests';

export interface CashDrawerEvent {
  id: string;
  sessionId: string;
  tenantId: string;
  eventType: 'drop' | 'adjustment' | 'note';
  amountCents: number;
  note: string | null;
  recordedBy: string | null;
  recordedAt: number;
  locationId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: number;
}

export interface CashDrawerSessionSummary {
  id: string;
  tenantId: string;
  locationId: string | null;
  status: 'open' | 'closed';
  openingFloatCents: number;
  expectedCloseCents: number | null;
  actualCloseCents: number | null;
  overShortCents: number | null;
  recordedCashSalesCents: number;
  recordedDropsCents: number;
  openingNotes: string | null;
  closingNotes: string | null;
  openedBy: string | null;
  closedBy: string | null;
  openedAt: number;
  closedAt: number | null;
  createdAt: number;
  updatedAt: number;
  lastComputedAt: number | null;
  cashSalesCents: number;
  dropsCents: number;
  expectedCents: number;
  events: CashDrawerEvent[];
}

export interface CashDrawerSessionResponse {
  ok: boolean;
  session: CashDrawerSessionSummary | null;
  history: Array<CashDrawerSessionSummary | null>;
}

export interface PosOrderLookupResult {
  id: string;
  code?: string | null;
  status: string;
  paymentStatus?: string | null;
  type?: string | null;
  source?: string | null;
  totalCents: number;
  currency: string;
  createdAt?: number | null;
  placedAt?: number | null;
  customerName?: string | null;
  customerPhone?: string | null;
  locationName?: string | null;
}

export interface PosOrderLookupResponse {
  ok: boolean;
  results: PosOrderLookupResult[];
}

export interface PosThrottleSummary {
  stationId: string;
  stationCode: string | null;
  stationName: string | null;
  locationId: string | null;
  baseMaxOrdersPerMinute: number | null;
  baseMaxItemsPerMinute: number | null;
  queuedItems: number;
  activeThrottle: {
    id: string;
    isHardStop: boolean;
    maxOrdersPerMinute: number | null;
    maxItemsPerMinute: number | null;
    endsAt: number | null;
    reason: string | null;
    notes: string | null;
    startedAt: number;
  } | null;
}

export interface PosThrottleResponse {
  ok: boolean;
  stations: PosThrottleSummary[];
}

const CashDrawerEventSchema = z
  .object({
    id: z.string(),
    sessionId: z.string(),
    tenantId: z.string(),
    eventType: z.enum(['drop', 'adjustment', 'note']),
    amountCents: z.number(),
    note: z.string().nullable(),
    recordedBy: z.string().nullable(),
    recordedAt: z.number(),
    locationId: z.string().nullable(),
    metadata: z.record(z.unknown()).nullable(),
    createdAt: z.number(),
  })
  .passthrough();

const CashDrawerSessionSummarySchema = z
  .object({
    id: z.string(),
    tenantId: z.string(),
    locationId: z.string().nullable(),
    status: z.enum(['open', 'closed']),
    openingFloatCents: z.number(),
    expectedCloseCents: z.number().nullable(),
    actualCloseCents: z.number().nullable(),
    overShortCents: z.number().nullable(),
    recordedCashSalesCents: z.number(),
    recordedDropsCents: z.number(),
    openingNotes: z.string().nullable(),
    closingNotes: z.string().nullable(),
    openedBy: z.string().nullable(),
    closedBy: z.string().nullable(),
    openedAt: z.number(),
    closedAt: z.number().nullable(),
    createdAt: z.number(),
    updatedAt: z.number(),
    lastComputedAt: z.number().nullable(),
    cashSalesCents: z.number(),
    dropsCents: z.number(),
    expectedCents: z.number(),
    events: z.array(CashDrawerEventSchema),
  })
  .passthrough();

const CashDrawerSessionResponseSchema = z
  .object({
    ok: z.boolean(),
    session: CashDrawerSessionSummarySchema.nullable(),
    history: z.array(CashDrawerSessionSummarySchema.nullable()),
  })
  .passthrough();

const PosOrderLookupResultSchema = z
  .object({
    id: z.string(),
    code: z.string().nullable().optional(),
    status: z.string(),
    paymentStatus: z.string().nullable().optional(),
    type: z.string().nullable().optional(),
    source: z.string().nullable().optional(),
    totalCents: z.number(),
    currency: z.string(),
    createdAt: z.number().nullable().optional(),
    placedAt: z.number().nullable().optional(),
    customerName: z.string().nullable().optional(),
    customerPhone: z.string().nullable().optional(),
    locationName: z.string().nullable().optional(),
  })
  .passthrough();

const PosOrderLookupResponseSchema = z
  .object({
    ok: z.boolean(),
    results: z.array(PosOrderLookupResultSchema),
  })
  .passthrough();

const PosThrottleSummarySchema = z
  .object({
    stationId: z.string(),
    stationCode: z.string().nullable(),
    stationName: z.string().nullable(),
    locationId: z.string().nullable(),
    baseMaxOrdersPerMinute: z.number().nullable(),
    baseMaxItemsPerMinute: z.number().nullable(),
    queuedItems: z.number(),
    activeThrottle: z
      .object({
        id: z.string(),
        isHardStop: z.boolean(),
        maxOrdersPerMinute: z.number().nullable(),
        maxItemsPerMinute: z.number().nullable(),
        endsAt: z.number().nullable(),
        reason: z.string().nullable(),
        notes: z.string().nullable(),
        startedAt: z.number(),
      })
      .nullable(),
  })
  .passthrough();

const PosThrottleResponseSchema = z
  .object({
    ok: z.boolean(),
    stations: z.array(PosThrottleSummarySchema),
  })
  .passthrough();

export const fetchCashDrawerSession = (params?: {
  locationId?: string | null;
  historyLimit?: number;
}) => {
  const searchParams = new URLSearchParams();
  if (params?.locationId) {
    searchParams.set('locationId', params.locationId);
  }
  if (typeof params?.historyLimit === 'number') {
    searchParams.set('historyLimit', String(params.historyLimit));
  }
  const suffix = searchParams.toString();
  return requestJson<CashDrawerSessionResponse>(`/pos/cash-drawer/session${suffix ? `?${suffix}` : ''}`, {
    schema: CashDrawerSessionResponseSchema,
  });
};

export const openCashDrawer = (payload: {
  openingFloatCents: number;
  locationId?: string | null;
  notes?: string | null;
}) =>
  requestJson<{ ok: boolean; session: CashDrawerSessionSummary }>('/pos/cash-drawer/open', {
    method: 'POST',
    body: {
      openingFloatCents: payload.openingFloatCents,
      locationId: payload.locationId ?? undefined,
      notes: payload.notes ?? undefined,
    },
    schema: z
      .object({
        ok: z.boolean(),
        session: CashDrawerSessionSummarySchema,
      })
      .passthrough(),
  });

export const closeCashDrawer = (payload: {
  sessionId: string;
  actualCloseCents: number;
  expectedCloseCents?: number | null;
  notes?: string | null;
}) =>
  requestJson<{ ok: boolean; session: CashDrawerSessionSummary }>('/pos/cash-drawer/close', {
    method: 'POST',
    body: {
      sessionId: payload.sessionId,
      actualCloseCents: payload.actualCloseCents,
      expectedCloseCents: payload.expectedCloseCents ?? undefined,
      notes: payload.notes ?? undefined,
    },
    schema: z
      .object({
        ok: z.boolean(),
        session: CashDrawerSessionSummarySchema,
      })
      .passthrough(),
  });

export const recordCashDrawerDrop = (payload: {
  sessionId: string;
  amountCents: number;
  note?: string | null;
}) =>
  requestJson<{ ok: boolean; session: CashDrawerSessionSummary }>('/pos/cash-drawer/drop', {
    method: 'POST',
    body: {
      sessionId: payload.sessionId,
      amountCents: payload.amountCents,
      note: payload.note ?? undefined,
    },
    schema: z
      .object({
        ok: z.boolean(),
        session: CashDrawerSessionSummarySchema,
      })
      .passthrough(),
  });

export const searchPosOrders = (query: string, options?: { limit?: number; locationId?: string | null }) => {
  const params = new URLSearchParams();
  params.set('query', query);
  if (options?.limit) {
    params.set('limit', String(options.limit));
  }
  if (options?.locationId) {
    params.set('locationId', options.locationId);
  }
  const suffix = params.toString();
  return requestJson<PosOrderLookupResponse>(`/pos/orders/lookup?${suffix}`, {
    schema: PosOrderLookupResponseSchema,
  });
};

export const fetchPosThrottles = () =>
  requestJson<PosThrottleResponse>('/pos/throttles', { schema: PosThrottleResponseSchema });

export const updatePosThrottle = (
  stationId: string,
  payload: {
    mode: 'pause' | 'resume' | 'limit';
    durationMinutes?: number;
    maxOrdersPerMinute?: number;
    maxItemsPerMinute?: number;
    reason?: string | null;
    notes?: string | null;
  },
) =>
  requestJson<PosThrottleResponse>(`/pos/throttles/${stationId}`, {
    method: 'POST',
    body: {
      mode: payload.mode,
      durationMinutes: payload.durationMinutes,
      maxOrdersPerMinute: payload.maxOrdersPerMinute,
      maxItemsPerMinute: payload.maxItemsPerMinute,
      reason: payload.reason ?? undefined,
      notes: payload.notes ?? undefined,
    },
    schema: PosThrottleResponseSchema,
  });
